import { useTranslation } from 'react-i18next';
import type { ResolvedConfigurationValue } from '@/types/studio';
import { buildLadder, humanizeValue, type LadderRung } from './provenance-ladder';
import styles from './ProvenanceLadder.module.css';

/**
 * The "why" behind one resolved value, drawn as the resolver actually works: six rungs,
 * Platform → Campus, each layer allowed to overwrite the one above, the lowest rung that
 * spoke winning.
 *
 * AN SVG RATHER THAN A LIST, because the thing being explained is spatial: "further down
 * wins" is one glance at a picture and a paragraph of prose. The data behind it is
 * buildLadder() in provenance-ladder.ts — a pure function with its own test — so this file
 * is only geometry.
 *
 * THEME-AWARE BY CONSTRUCTION. Every colour is a CSS variable or currentColor applied
 * through the module stylesheet; the SVG carries no hardcoded hex, so a future dark theme
 * restyles it by overriding the same tokens every other surface uses.
 */

const ROW_HEIGHT = 52;
const TOP = 14;
const RAIL_X = 22;
const TEXT_X = 44;
const WIDTH = 480;
const CHIP_RIGHT = WIDTH - 10;
const CHIP_HEIGHT = 22;
/** IBM Plex Sans at 12px averages ~6.6px per character; the +18 is the chip's padding. */
const chipWidth = (text: string) => Math.min(210, Math.round(text.length * 6.6) + 18);

function rungClass(status: LadderRung['status']): string {
  if (status === 'winner') return styles.rungWinner;
  if (status === 'overridden') return styles.rungOverridden;
  return styles.rungSilent;
}

export function ProvenanceLadder({ resolved }: { resolved: ResolvedConfigurationValue }) {
  const { t } = useTranslation();
  const rungs = buildLadder(resolved);
  const height = TOP + rungs.length * ROW_HEIGHT;
  const winnerValue = humanizeValue(resolved.value);

  return (
    <svg
      className={styles.ladder}
      viewBox={`0 0 ${WIDTH} ${height}`}
      role="img"
      aria-label={t('studio.ladder.aria', {
        value: winnerValue,
        layer: resolved.source.label,
      })}
    >
      <title>
        {t('studio.ladder.title', { value: winnerValue, layer: resolved.source.label })}
      </title>

      {/* The rail: one line from the least specific layer to the most specific. */}
      <line
        className={styles.rail}
        x1={RAIL_X}
        y1={TOP + ROW_HEIGHT / 2}
        x2={RAIL_X}
        y2={TOP + (rungs.length - 1) * ROW_HEIGHT + ROW_HEIGHT / 2}
        aria-hidden="true"
      />

      {rungs.map((rung, index) => {
        const cy = TOP + index * ROW_HEIGHT + ROW_HEIGHT / 2;
        const scopeName = t(`studio.scope.${rung.scope}`);
        const statusText =
          rung.status === 'winner'
            ? t('studio.ladder.winner')
            : rung.status === 'overridden'
              ? t('studio.ladder.overridden')
              : t('studio.ladder.silent');
        const chip = rung.status === 'winner' ? humanizeValue(rung.value ?? '') : null;
        const cw = chip ? chipWidth(chip) : 0;

        return (
          <g key={rung.scope} className={rungClass(rung.status)}>
            {/* Per-rung description, so a screen reader walking the image hears the
                whole chain, not only the summary title. */}
            <title>{`${scopeName} — ${statusText}${rung.label ? ` (${rung.label})` : ''}`}</title>

            {/* The winning rung gets a halo so it reads as "this one" before any text does. */}
            {rung.status === 'winner' && (
              <circle className={styles.halo} cx={RAIL_X} cy={cy} r={11} aria-hidden="true" />
            )}
            <circle
              className={styles.dot}
              cx={RAIL_X}
              cy={cy}
              r={rung.status === 'silent' ? 4 : 6.5}
              aria-hidden="true"
            />

            <text className={styles.scopeName} x={TEXT_X} y={cy - 3}>
              {scopeName}
              <tspan className={styles.statusTag} dx="8">
                {statusText}
              </tspan>
            </text>
            <text className={styles.layerLabel} x={TEXT_X} y={cy + 13}>
              {rung.label
                ? rung.scopeKey && rung.scopeKey !== '*'
                  ? `${rung.label} · ${rung.scopeKey}`
                  : rung.label
                : t('studio.ladder.noOpinion')}
            </text>

            {chip && (
              <g aria-hidden="true">
                <rect
                  className={styles.chip}
                  x={CHIP_RIGHT - cw}
                  y={cy - CHIP_HEIGHT / 2}
                  width={cw}
                  height={CHIP_HEIGHT}
                  rx={CHIP_HEIGHT / 2}
                />
                <text className={styles.chipText} x={CHIP_RIGHT - cw / 2} y={cy + 4} textAnchor="middle">
                  {chip}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
